/**
 * useDeclarations - Hook for fetching declarations from Firestore (Admin)
 *
 * Provides cached, type-safe access to declaration templates with automatic
 * loading and error states via React Query.
 *
 * Usage:
 * ```tsx
 * const { declarations, isLoading, error, refetch } = useDeclarations();
 * const { declarations } = useDeclarations({ status: 'active' });
 * ```
 */

import { where, orderBy, QueryConstraint } from 'firebase/firestore';
import { useFirestoreQuery, schoolCollection } from './useFirestoreQuery';
import { queryKeys } from './queryKeys';
import { useAuth } from './useAuth';
import type { Declaration } from '@shared/types';

export interface UseDeclarationsOptions {
  /** Filter by active status */
  active?: boolean;
  /** Override schoolId (defaults to user's schoolId) */
  schoolId?: string;
  /** Whether to fetch (defaults to true when schoolId is available) */
  enabled?: boolean;
}

export interface UseDeclarationsResult {
  /** List of declarations */
  declarations: Declaration[];
  /** Loading state */
  isLoading: boolean;
  /** Error if query failed */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether data is being refetched in background */
  isRefetching: boolean;
  /** Active declarations count */
  activeCount: number;
  /** Inactive declarations count */
  inactiveCount: number;
}

/**
 * Hook for fetching declarations (typically used by admin)
 */
export function useDeclarations(options: UseDeclarationsOptions = {}): UseDeclarationsResult {
  const { customClaims } = useAuth();

  const schoolId = options.schoolId || customClaims?.schoolId;

  // Build query constraints
  const constraints: QueryConstraint[] = [];

  if (options.active !== undefined) {
    constraints.push(where('active', '==', options.active));
  }

  // Order by creation date (newest first)
  constraints.push(orderBy('metadata.createdAt', 'desc'));

  // Determine query key based on filters
  const queryKey = options.active === true
    ? queryKeys.school.declarations.active(schoolId || '')
    : queryKeys.school.declarations.all(schoolId || '');

  const {
    data = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useFirestoreQuery<Declaration>({
    queryKey,
    collectionPath: schoolCollection(schoolId || '', 'declarations'),
    constraints,
    enabled: options.enabled ?? !!schoolId,
    idField: 'declarationId',
  });

  // Compute counts
  const activeCount = data.filter((d) => d.active).length;
  const inactiveCount = data.filter((d) => !d.active).length;

  return {
    declarations: data,
    isLoading,
    error,
    refetch,
    isRefetching,
    activeCount,
    inactiveCount,
  };
}

/**
 * Hook for fetching a single declaration by ID
 */
export function useDeclaration(declarationId: string | undefined) {
  const { customClaims } = useAuth();
  const schoolId = customClaims?.schoolId;

  const { data, isLoading, error, refetch } = useFirestoreQuery<Declaration>({
    queryKey: queryKeys.school.declarations.single(schoolId || '', declarationId || ''),
    collectionPath: schoolCollection(schoolId || '', 'declarations'),
    constraints: [where('declarationId', '==', declarationId)],
    enabled: !!schoolId && !!declarationId,
    idField: 'declarationId',
  });

  return {
    declaration: data?.[0] || null,
    isLoading,
    error,
    refetch,
  };
}
